import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableBox from '../../../../components/TableBox';
import StatusBadge from '../../../../components/StatusBadge';
import RowActions from '../../../../components/RowActions';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import CustomizedSwitch from '../../../../components/Switch';
import useFetch from '../../../../hooks/useFetch';
import usePagination from '../../../../hooks/usePagination';
import MasterLookupService from '../../../../services/master-lookup.service';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { SortOrder, Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';

import useSorting from '../../../../hooks/useSorting';
import { sortData } from '../../../../utils/sortUtils';

interface BillingCycleRow {
  id: number;
  cycle_code: string;
  cycle_name: string;
  duration_in_months: number;
  status: string;
}

const BillingCycleList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'cycle_code' });
  const [items, setItems] = useState<BillingCycleRow[]>([]);
  const [total, setTotal] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BillingCycleRow | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const { loading } = useFetch({
    service: MasterLookupService,
    func: 'list',
    payload: { type: 'billingCycles', includeDeleted: showInactive, page, limit: perPage },
    dependency: [showInactive, page, perPage, refreshToken],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`${Modules.masters}${AuthRoutes.billingCycles}${RouteParamsTypes.edit}/${id}`);
    },
    [navigate]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      const response: any = await MasterLookupService.remove('billingCycles', deleteTarget.id);
      const softOrHard = response?.data?.softOrHard;
      toast.success(
        <Toast
          message={
            softOrHard === 'soft'
              ? t('masters.billingCycles.list.pageTexts.deleteSuccessSoft')
              : t('masters.billingCycles.list.pageTexts.deleteSuccessHard')
          }
        />,
        { icon: false }
      );
      setRefreshToken((v) => v + 1);
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, t]);

  const headCells: HeadCellType[] = useMemo(
    () => [
      { id: 'cycle_code', sortEnabled: true, width: '20%', label: t('masters.billingCycles.list.pageTexts.table.columns.code') },
      { id: 'cycle_name', sortEnabled: true, width: '25%', label: t('masters.billingCycles.list.pageTexts.table.columns.name') },
      { id: 'duration_in_months', sortEnabled: true, width: '20%', label: t('masters.billingCycles.list.pageTexts.table.columns.duration') },
      { id: 'status', sortEnabled: true, width: '20%', label: t('masters.billingCycles.list.pageTexts.table.columns.status') },
      { id: 'actions', sortEnabled: false, width: '15%', label: t('masters.billingCycles.list.pageTexts.table.columns.actions'), textAlign: 'right' },
    ],
    [t]
  );

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: BillingCycleRow; className?: string }) => (
      <tr className={className}>
        <td>{item.cycle_code}</td>
        <td>{item.cycle_name}</td>
        <td>{item.duration_in_months}</td>
        <td>
          <StatusBadge label={item.status} colour={item.status === 'active' ? '#2e7d32' : '#757575'} />
        </td>
        <td className="text-end">
          <RowActions
            actions={[
              {
                id: 'edit',
                label: t('common.edit') || 'Edit',
                icon: <i className="ri-pencil-line" />,
                onClick: () => handleEdit(item.id),
              },
              {
                id: 'delete',
                label: t('common.delete') || 'Delete',
                icon: <i className="ri-delete-bin-line" />,
                danger: true,
                onClick: () => setDeleteTarget(item),
              },
            ]}
          />
        </td>
      </tr>
    ),
    [handleEdit]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('masters.billingCycles.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, loading, sortedItems, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('masters.billingCycles.list.pageTexts.title')}</h4>
        <div className="d-flex align-items-center gap-4">
          <CustomizedSwitch
            label={t('masters.billingCycles.list.pageTexts.showInactive')}
            name="showInactive"
            checked={showInactive}
            onChange={() => setShowInactive((v) => !v)}
          />
          <Button
            className="btn btn-primary"
            text={t('masters.billingCycles.list.pageTexts.addNew')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.billingCycles}${RouteParamsTypes.add}`)}
          />
        </div>
      </div>

      <TableBox tableValues={values} />

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('masters.billingCycles.list.pageTexts.deleteDialog.title')}
        content={t('masters.billingCycles.list.pageTexts.deleteDialog.body')}
        action={
          <>
            <Button
              className="btn btn-outline-secondary"
              text={t('masters.billingCycles.list.pageTexts.deleteDialog.cancel')}
              onClick={() => setDeleteTarget(null)}
            />
            <Button
              className="btn btn-danger"
              text={t('masters.billingCycles.list.pageTexts.deleteDialog.confirm')}
              onClick={handleDeleteConfirm}
            />
          </>
        }
      />
    </>
  );
};

export default BillingCycleList;
